import { BaseContract } from '../common/base.contract';
import { Contract, ethers, JsonRpcProvider } from 'ethers';
import { toNumber } from '../common/base-cdk-contract';
import CdkFacetAbi from '../../abi/Facets/CdkFacet.json';
import { IERC20, ERC20Permit } from '../../model/erc20.model';
import { formatUsdc, parseCdk } from '../../utils/web3-convert.utils';

export class CdkFacet extends BaseContract implements IERC20 {
    constructor(address: string, provider: JsonRpcProvider) {
        super(new Contract(address, CdkFacetAbi.abi, provider));
    }

    // Méthodes ERC20 standard
    name = async (): Promise<string> => {
        return await this.contract.name();
    }

    symbol = async (): Promise<string> => {
        return await this.contract.symbol();
    }

    decimals = async (): Promise<number> => {
        return await this.contract.decimals();
    }

    totalSupply = async (): Promise<number> => {
        const supply = await this.contract.totalSupply();
        return toNumber(supply);
    }

    balanceOf = async (account: string): Promise<number> => {
        return await this.contract.balanceOf(account);
    }

    allowance = async (owner: string, spender: string): Promise<number> => {
        const allowed = await this.contract.allowance(owner, spender);
        return toNumber(allowed);
    }

    // Méthodes d'écriture ERC20
    approve = async (contractAddress: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const amountWithDecimals = ethers.parseUnits(amount.toString(), 18);
        const tx = await contractWithSigner.approve(contractAddress, amountWithDecimals);
        await tx.wait();
    }

    transfer = async (recipient: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.transfer(recipient, parseCdk(amount));
        await tx.wait();
    }

    transferFrom = async (sender: string, recipient: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.transferFrom(sender, recipient, amount);
        await tx.wait();
    }

    // Extensions ERC20
    permit = async (permitData: ERC20Permit) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.permit(
            permitData.owner,
            permitData.spender,
            permitData.value,
            permitData.deadline,
            permitData.v,
            permitData.r,
            permitData.s
        );
        await tx.wait();
    }

    // Méthodes spécifiques CDK
    mint = async (to: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.mint(to, amount);
        await tx.wait();
    }

    burn = async (address: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.burn(address, amount);
        await tx.wait();
    }

    // Nouvelle méthode
    doTransferFromSelf = async (to: string, amount: number): Promise<boolean> => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doTransferFromSelf(to, amount);
        await tx.wait();
        return true;
    }

    // Méthodes de lecture supplémentaires
    nonces = async (owner: string): Promise<number> => {
        const nonce = await this.contract.nonces(owner);
        return toNumber(nonce);
    }

    DOMAIN_SEPARATOR = async (): Promise<string> => {
        return await this.contract.DOMAIN_SEPARATOR();
    }

    // Méthodes d'écriture supplémentaires
    burnFrom = async (account: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.burnFrom(account, amount);
        await tx.wait();
    }

    increaseAllowance = async (spender: string, addedValue: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.increaseAllowance(spender, addedValue);
        await tx.wait();
    }

    decreaseAllowance = async (spender: string, subtractedValue: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.decreaseAllowance(spender, subtractedValue);
        await tx.wait();
    }

    doMint = async (to: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doMint(to, amount);
        await tx.wait();
    }

    doBurn = async (amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doBurn(amount);
        await tx.wait();
    }

    doBurnFrom = async (account: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doBurnFrom(account, amount);
        await tx.wait();
    }

    doTransfer = async (to: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doTransfer(to, amount);
        await tx.wait();
    }

    // Méthodes liées aux snapshots
    doClaimFromSnapshot = async () => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doClaimFromSnapshot();
        await tx.wait();
    }

    doCreateSnapshot = async (snapshotId: string) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doCreateSnapshot(snapshotId);
        await tx.wait();
    }

    getSnapshotBalance = async (snapshotId: string, account: string): Promise<number> => {
        const balance = await this.contract.getSnapshotBalance(snapshotId, account);
        return toNumber(balance);
    }

    getSnapshotTotalSupply = async (snapshotId: string): Promise<number> => {
        const supply = await this.contract.getSnapshotTotalSupply(snapshotId);
        return toNumber(supply);
    }
} 
